import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  InputGroup,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from 'react-bootstrap';
import ModalWrapper from 'components/global/ModalWrapper/ModalWrapper';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import edoFundingDisbursementSignal, {
  edoFundingTransactionAddTransactionFormSignal,
} from './Disbursement.signals';
import {
  handleAddTransactionFormChange,
  handleChangeAttachmentName,
  handleRemoveAttachment,
  handleSubmitTransaction,
  hideAddManualTransactionModal,
} from './Disbursement.helpers';
import AttachmentListForm from 'components/global/AttachmentsListForm';
import AddAttachmentsButton from 'components/global/AddAttachmentsButton';

function parseCurrency(value) {
  if (!value || String(value).trim() === '') return null;
  const cleaned = String(value).replace(/[$,\s]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
}

const DisbursementAddManualTransactionModal = () => {
  const {
    createTransactionModalOpen,
    record,
  } = edoFundingDisbursementSignal.value;

  const { amount, date, time, memo, attachments } =
    edoFundingTransactionAddTransactionFormSignal.value;

  const [amountFocused, setAmountFocused] = useState(false);
  const [amountRaw, setAmountRaw] = useState('');

  useEffect(() => {
    if (createTransactionModalOpen) {
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const timeInSeconds = now.getHours() * 3600 + now.getMinutes() * 60;
      edoFundingTransactionAddTransactionFormSignal.update({
        date: dateStr,
        time: timeInSeconds,
      });
    }
  }, [createTransactionModalOpen]);

  const transactionAmount = Number(amount);
  const existingSum = (record?.transactions || []).reduce((acc, t) => acc + t.amount, 0);
  const wouldExceed =
    !Number.isNaN(transactionAmount) &&
    transactionAmount > 0 &&
    existingSum + transactionAmount > (record?.amount ?? 0);

  const canSave =
    record?.id &&
    amount &&
    date &&
    memo?.trim() &&
    !wouldExceed;

  return (
    <ModalWrapper
        show={createTransactionModalOpen}
        centered
        size="lg"
        animation
        onExited={() => {
          setAmountFocused(false);
          setAmountRaw('');
        }}
      >
      <ModalHeader>
        <ModalTitle>Add Transaction</ModalTitle>
        <CloseModalButton onCloseModal={hideAddManualTransactionModal} />
      </ModalHeader>
      <ModalBody>
        {wouldExceed && (
          <Alert variant="danger" className="mb-3">
            The total of all transactions cannot exceed the disbursement amount.
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Label>Amount (USD) <span className="text-danger">*</span></Form.Label>
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="text"
              value={
                amountFocused
                  ? amountRaw
                  : (() => {
                      const num = amount === '' ? null : Number(amount);
                      if (num != null && !Number.isNaN(num) && Number.isFinite(num)) {
                        return new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(num);
                      }
                      return '';
                    })()
              }
              onFocus={() => {
                setAmountFocused(true);
                const num = amount === '' ? null : Number(amount);
                setAmountRaw(
                  num != null && !Number.isNaN(num)
                    ? num.toFixed(2)
                    : ''
                );
              }}
              onBlur={() => {
                setAmountFocused(false);
                const parsed = parseCurrency(amountRaw);
                handleAddTransactionFormChange({
                  target: { name: 'amount', value: parsed != null ? String(parsed) : '' },
                });
                setAmountRaw('');
              }}
              onChange={(e) => setAmountRaw(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
            />
          </InputGroup>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Memo <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Enter memo"
            name="memo"
            value={memo}
            onChange={handleAddTransactionFormChange}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Created Date <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="date"
            name="date"
            value={date || ''}
            onChange={handleAddTransactionFormChange}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Created Time <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="time"
            value={
              (() => {
                const h = Math.floor(time / 3600);
                const m = Math.floor((time % 3600) / 60);
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
              })()
            }
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return handleAddTransactionFormChange({ target: { name: 'time', value: 0 } });
              const [h, m] = v.split(':').map(Number);
              handleAddTransactionFormChange({
                target: { name: 'time', value: (h || 0) * 3600 + (m || 0) * 60 },
              });
            }}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="mb-0">
            Attachments <span className="text-muted font-weight-normal">(optional)</span>
          </Form.Label>
          <div className="mt-2">
            <AddAttachmentsButton
              multiple={true}
              onChange={(e) =>
                handleAddTransactionFormChange({
                  target: { value: e.target.files, name: 'attachments' },
                })
              }
            />
          </div>
          <AttachmentListForm
            onChange={handleChangeAttachmentName}
            onRemove={handleRemoveAttachment}
            attachments={attachments}
          />
        </Form.Group>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="outline-secondary"
          className="rounded"
          onClick={() => hideAddManualTransactionModal()}
        >
          Cancel
        </Button>
        <Button
          className="rounded"
          disabled={!canSave}
          onClick={() => handleSubmitTransaction()}
        >
          Save Changes
        </Button>
      </ModalFooter>
    </ModalWrapper>
  );
};

export default DisbursementAddManualTransactionModal;
